import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { AccountLayout } from "../components/shell.tsx";
import { Alert, Button, PageHeader, Panel, PasswordField, TextField } from "../components/ui.tsx";
import { ApiError, applyFieldErrors, patch, post } from "../lib/api.ts";
import { ME_KEY, useSession } from "../lib/session.tsx";
import { passwordSchema, profileSchema, type SessionDto, type UserDto, PASSWORD_HINT } from "../../shared/auth.ts";
import { lastPlan, usePageTitle } from "../lib/nav.tsx";
import { BackLink } from "../components/nav-controls.tsx";

const passwordForm = z
  .object({ current_password: z.string().min(1, "Informe a senha atual."), new_password: passwordSchema, confirm: z.string() })
  .refine((v) => v.new_password === v.confirm, { path: ["confirm"], message: "As senhas não conferem." });

export function ProfilePage() {
  usePageTitle("Meu perfil");
  const [backTo] = useState(() => { const p = lastPlan(); return p ? `/planos/${p}/painel` : "/planos"; });
  const { user, adopt } = useSession();
  const qc = useQueryClient();
  const [nameMsg, setNameMsg] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [pwMsg, setPwMsg] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  const nameForm = useForm<z.input<typeof profileSchema>>({ resolver: zodResolver(profileSchema), values: { name: user?.name ?? "" } });
  const pwForm = useForm<z.input<typeof passwordForm>>({
    resolver: zodResolver(passwordForm),
    defaultValues: { current_password: "", new_password: "", confirm: "" },
  });

  const saveName = nameForm.handleSubmit(async (v) => {
    setNameMsg(null);
    try {
      const res = await patch<{ data: UserDto }>("/auth/profile", v);
      qc.setQueryData(ME_KEY, res.data);
      setNameMsg({ tone: "success", text: "Nome atualizado." });
    } catch (e) {
      applyFieldErrors(e, nameForm.setError as never);
      setNameMsg({ tone: "danger", text: e instanceof ApiError ? e.message : "Não foi possível salvar." });
    }
  });

  const savePassword = pwForm.handleSubmit(async (v) => {
    setPwMsg(null);
    try {
      const res = await post<{ data: SessionDto }>("/auth/change-password", { current_password: v.current_password, new_password: v.new_password });
      adopt(res.data);
      pwForm.reset();
      setPwMsg({ tone: "success", text: "Senha alterada. As outras sessões desta conta foram encerradas." });
    } catch (e) {
      applyFieldErrors(e, pwForm.setError as never);
      setPwMsg({ tone: "danger", text: e instanceof ApiError ? e.message : "Não foi possível alterar a senha." });
    }
  });

  return (
    <AccountLayout>
      <PageHeader
        back={<BackLink fallback={backTo} label={backTo === "/planos" ? "Minhas oficinas" : "Voltar para a oficina"} />}
        title="Meu perfil"
        subtitle="Dados da sua conta individual."
      />
      <div className="grid gap-5 md:grid-cols-2">
        <Panel>
          <h2 className="text-lg font-bold">Identificação</h2>
          {nameMsg && <Alert tone={nameMsg.tone}>{nameMsg.text}</Alert>}
          <form onSubmit={saveName} noValidate>
            <TextField label="Nome" autoComplete="name" error={nameForm.formState.errors.name?.message} {...nameForm.register("name")} />
            <TextField label="E-mail" value={user?.email ?? ""} readOnly hint="Para alterar o e-mail, fale com o instrutor." />
            <Button type="submit" loading={nameForm.formState.isSubmitting} disabled={!nameForm.formState.isDirty}>Salvar nome</Button>
          </form>
        </Panel>
        <Panel>
          <h2 className="text-lg font-bold">Alterar senha</h2>
          {pwMsg && <Alert tone={pwMsg.tone}>{pwMsg.text}</Alert>}
          <form onSubmit={savePassword} noValidate>
            <PasswordField label="Senha atual" autoComplete="current-password" error={pwForm.formState.errors.current_password?.message} {...pwForm.register("current_password")} />
            <PasswordField label="Nova senha" autoComplete="new-password" hint={PASSWORD_HINT} error={pwForm.formState.errors.new_password?.message} {...pwForm.register("new_password")} />
            <PasswordField label="Confirmar nova senha" autoComplete="new-password" error={pwForm.formState.errors.confirm?.message} {...pwForm.register("confirm")} />
            <Button type="submit" loading={pwForm.formState.isSubmitting}>Alterar senha</Button>
          </form>
        </Panel>
      </div>
    </AccountLayout>
  );
}
